import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnInit,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  EventEmitter,
  Output
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient } from '@delon/theme';
import { NzSelectModeType, NzSelectComponent } from 'ng-zorro-antd/select';
import { Subject } from 'rxjs';

import { PurchaserAddConfigService } from '../../inline-add-panel/purchaser-add-config.service';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';
@Component({
  selector: 'app-purchaser-select',
  templateUrl: './purchaser-select.component.html',
  styles: [
    `
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      .purchaser-select-container {
        position: relative;
        width: 100%;
      }

      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PurchaserSelectComponent),
      multi: true
    }
  ]
})
export class PurchaserSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() activeOnly = true;
  @Input() mode: NzSelectModeType = 'default';
  @Input() defaultType?: 'purchaser' = 'purchaser';
  @Input() selectDisabled: boolean = false;
  @Input() tabindex: string | null = null;
  @Input() nzAllowClear: boolean = true;
  @Input() skipRestrictions = false;

  @Output() readonly addNewPurchaser = new EventEmitter<any>();

  NotShowInReport: boolean = false;
  destroy$ = new Subject<void>();

  purchaserConfig: any;
  inlineDefaults: any = null;
  // Store instant data separately to preserve it when dropdown closes

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private el: ElementRef,
    private purchaserAddConfigService: PurchaserAddConfigService
  ) {
    super(http, cdr, router);
    this.valueField = '';
  }
  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.purchaserConfig = this.purchaserAddConfigService.getPurchaserConfig();

    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        const trimmedValue = value.trim();
        this.storedInstantData = trimmedValue;
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = trimmedValue;
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }
  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            const trimmedValue = event.target.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }
  }

  getUrl(searchValue: string): string {
    if (searchValue) {
      // Check if this is a valueField match request (indicated by special prefix)
      const isValueFieldMatch = searchValue.startsWith(
        SelectBaseComponent.VALUEFIELD_MATCH_PREFIX
      );
      const actualSearchValue = isValueFieldMatch
        ? searchValue.substring(
            SelectBaseComponent.VALUEFIELD_MATCH_PREFIX.length
          )
        : searchValue;

      const searchStr = actualSearchValue.replaceAll("'", "''");

      // Check if searchValue can be converted to int (ID)
      const searchValueNum = parseInt(actualSearchValue, 10);
      const isNumericId =
        !isNaN(searchValueNum) &&
        searchValueNum.toString() === actualSearchValue.trim();

      if (isValueFieldMatch) {
        // ValueField match: use eq operator for precise matching based on valueField
        // Ignore prerequisite conditions (like isActive) when matching by valueField
        // This ensures we can find records even if they are inactive
        let filter: string;
        if (this.valueField === 'id') {
          // Match by ID exactly
          filter = `id eq ${isNumericId ? searchValueNum : actualSearchValue}`;
        } else {
          // Match by the specified valueField exactly (code, name, etc.)
          // Use the valueField if set, otherwise default to 'code'
          const fieldName = this.valueField || 'code';
          filter = `${fieldName} eq '${encodeURIComponent(searchStr)}'`;
        }
        let url = `api/purchasers/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
        if (this.skipRestrictions) {
          url += `&skipRestrictions=true`;
        }
        return url;
      } else {
        // Partial match: use contains for search behavior with prerequisite conditions
        let filter = `isActive eq true`;
        if (!this.activeOnly) {
          filter += ` or isActive eq false`;
        }
        if (isNumericId) {
          // Include ID matching: (Id eq <id>) or (code/name contains search)
          filter = `(${filter}) and ((id eq ${searchValueNum}) or (contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}')))`;
        } else {
          // Original behavior: only search by code/name
          filter = `(${filter}) and (contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}'))`;
        }
        let url = `api/purchasers/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
        if (this.skipRestrictions) {
          url += `&skipRestrictions=true`;
        }
        return url;
      }
    } else {
      // No search value: apply prerequisite conditions
      let filter = `isActive eq true`;
      if (!this.activeOnly) {
        filter += ` or isActive eq false`;
      }
      let url = `api/purchasers/lookup?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
      if (this.skipRestrictions) {
        url += `&skipRestrictions=true`;
      }
      return url;
    }
  }

  jump(): string {
    return '/ap/purchaser/detail/0';
  }

  // set the param
  queryParam(): String {
    return 'PurchaserCode';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report') || curUrl.includes('einv/')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }
  override async writeValue(value: any) {
    // Extract ID if value is an object (e.g., from API response)
    let idValue = value;
    if (value && typeof value === 'object' && this.valueField) {
      idValue = value[this.valueField] || value.id;
    }

    this.value = idValue;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        // When valueField is set, we need to fetch the list to find the matching item
        // Use valueField match to find the record that exactly matches the valueField (ID or Code)
        // Prefix keyword with special marker to indicate valueField match (ignores prerequisite conditions)
        let keyword = `${SelectBaseComponent.VALUEFIELD_MATCH_PREFIX}${idValue}`;
        await this.fetchData(keyword);
        this.selected = this.list.find(x => x[this.valueField] == idValue);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        // Merge instant data into initialData as fallback
        if (finalInstantData) {
          this.inlineDefaults = {
            ...this.inlineDefaults,
            code: finalInstantData
          };
        }

        this.showInlineAddPanel = true;
        // Force change detection to ensure the panel is rendered
        this.cdr.detectChanges();

        // Retry mechanism to ensure changeInstantData is called after form is ready
        const trySetInstantData = (retries: number = 10) => {
          if (!finalInstantData) return;

          if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
            const instantFieldName =
              this.purchaserConfig?.fields?.find((f: any) => f.isInstantData)
                ?.name || 'code';
            const control = this.inlineAddPanel.formGroup.get(instantFieldName);
            if (control) {
              this.inlineAddPanel.changeInstantData(finalInstantData);
              return;
            }
          }

          if (retries > 0) {
            setTimeout(() => trySetInstantData(retries - 1), 50);
          }
        };

        // Try immediately and with retries
        setTimeout(() => {
          if (this.inlineAddPanel && finalInstantData) {
            this.inlineAddPanel.changeInstantData(finalInstantData);
          }
          trySetInstantData();
        }, 0);

        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }

  onPurchaserCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the purchaser object in the expected format
      const purchaser = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code || event.item.purchaserCode,
        name: event.item.name,
        isActive: event.item.isActive,
        isDefault: event.item.isDefault
      };

      // Add the new purchaser to the list and select it
      this.list.unshift(purchaser);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the purchaser object in the form control to trigger purchaser.valueChanges
      this.value = purchaser.id;
      this.selected = purchaser.id;

      // Set the purchaser object directly in the form control
      this.onChange(purchaser);
      this.writeValue(purchaser);
      // Emit addNewPurchaser event for parent component to handle
      this.addNewPurchaser.emit(purchaser);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onPurchaserCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onPurchaserMoreDetails(): void {
    // Navigate to purchaser details page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.name) queryParams.PurchaserName = formData.name;
      if (formData.code) queryParams.PurchaserCode = formData.code;
      if (formData.isActive !== undefined)
        queryParams.IsActive = formData.isActive;
      if (formData.isDefault !== undefined)
        queryParams.IsDefault = formData.isDefault;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/ap/purchaser/detail/0'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/ap/purchaser/detail/0'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // For purchaser, we don't need to load additional options like customer does
    return Promise.resolve();
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to purchaser/detail new()
    return new Promise(resolve => {
      this.http.get('api/purchasers/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          if (data?.isActive !== undefined) defaults.isActive = data.isActive;
          if (data?.isDefault !== undefined)
            defaults.isDefault = data.isDefault;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          this.inlineDefaults = null;
          resolve();
        }
      });
    });
  }
}
